import * as React from "react";
import { useTranslation } from 'react-i18next';
import axios from '../../../../utils/api/axiosInstance';

import Slide from "@mui/material/Slide";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Collapse from "@mui/material/Collapse";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";

import { ProgressModalProps, ProgressInterface } from "./ProgressInterface";

const Transition = React.forwardRef(function Transition(
  props: any,
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function getProgressValue(progressStatus: ProgressInterface | null): number {
  if (!progressStatus) return 0;

  if (progressStatus.status === "running") {
    return progressStatus.progress.percentage;
  }

  if (progressStatus.status === "completed") {
    return 100;
  }

  return 0;
}

function getTitle(progressStatus: ProgressInterface | null): string {
  if (!progressStatus) return "Idle";

  switch (progressStatus.status) {
    case "idle":
      return "Idle";
    case "scanning":
      return "Scanning";
    case "rebuilding":
      return "Rebuilding";
    case "running":
      return "Sync in progress";
    case "completed":
      return progressStatus.error ? "Completed with error" : "Completed";
    default:
      return "Status";
  }
}

export default function ProgressModal({
  open,
  statusEndpoint,
  onClose,
}: ProgressModalProps) {
  const { t } = useTranslation();

  const [showDetails, setShowDetails] = React.useState(false);
  const [progressStatus, setProgressStatus] = React.useState<ProgressInterface | null>(null);

  // pull the endpoint every 5 seconds when modal is open
  React.useEffect(() => {
    if (!open) {
      setProgressStatus(null);
      return;
    }

    const fetchStatus = async () => {
      try {
        const res = await axios.get(statusEndpoint);
        
        if (res.data?.success === false) {
          setProgressStatus({
            status: 'completed',
            lastCompleted: new Date().toISOString(),
            error: res.data.error || 'Failed to get status',
          });
          return;
        }

        setProgressStatus(res.data);
      } catch (err: any) {
        setProgressStatus({
          status: 'completed',
          lastCompleted: new Date().toISOString(),
          error: err.message || 'Failed to fetch status',
        });
      }
    };

    // fetch immediately when modal opens
    fetchStatus();

    // then pull every 5 seconds
    const intervalId = setInterval(fetchStatus, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [open, statusEndpoint]);

  const progressValue = getProgressValue(progressStatus);

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      sx={{
        "& .MuiDialog-paper": {
          margin: 0,
          width: "100%",
          position: "fixed",
          bottom: 0,
          borderRadius: "12px 12px 0 0",
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {getTitle(progressStatus)}
          </Typography>

          {onClose && (
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>

        <LinearProgress
          variant="determinate"
          value={progressValue}
          sx={{ height: 8, borderRadius: 4, mb: 1 }}
        />

        <Typography variant="body2" color="text.secondary">
          {progressValue}%
        </Typography>

        {progressStatus?.status === "idle" && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {progressStatus.message}
          </Typography>
        )}

        {(progressStatus?.status === "scanning" ||
          progressStatus?.status === "rebuilding") && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {progressStatus.message}
            </Typography>
          )}

        {progressStatus?.status === "running" && (
          <>
            <Box
              onClick={() => setShowDetails((p) => !p)}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                mt: 1,
                color: "text.secondary",
                userSelect: "none",
              }}
            >
              <Typography variant="body2">Details</Typography>
              <ExpandMoreIcon
                sx={{
                  ml: 0.5,
                  transform: showDetails ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "0.2s",
                }}
              />
            </Box>

            <Collapse in={showDetails}>
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  borderRadius: 1,
                  fontSize: "0.85rem",
                  lineHeight: 1.6,
                  color: "text.primary",
                  userSelect: "none",
                }}
              >
                <Box sx={{ display: "grid", gridTemplateColumns: "160px 1fr", rowGap: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('mdm.processed')}
                  </Typography>
                  <Typography variant="body2">
                    {progressStatus.progress.processed} /{" "}
                    {progressStatus.progress.total}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {t('mdm.succeeded')}
                  </Typography>
                  <Typography variant="body2">
                    {progressStatus.progress.succeeded}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {t('mdm.failed')}
                  </Typography>
                  <Typography variant="body2">
                    {progressStatus.progress.failed}
                  </Typography>

                  {progressStatus.progress.currentFile && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        {t('mdm.current_file')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ wordBreak: "break-all" }}
                      >
                        {progressStatus.progress.currentFile}
                      </Typography>
                    </>
                  )}

                  {progressStatus.progress.rate && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        {t('mdm.rate')}
                      </Typography>
                      <Typography variant="body2">
                        {progressStatus.progress.rate}
                      </Typography>
                    </>
                  )}

                  {progressStatus.progress.estimatedTimeRemaining && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        {t('mdm.eta')}
                      </Typography>
                      <Typography variant="body2">
                        {progressStatus.progress.estimatedTimeRemaining}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </Collapse>
          </>
        )}

        {progressStatus?.status === "completed" && progressStatus.error && (
          <Typography color="error" sx={{ mt: 1 }}>
            {progressStatus.error}
          </Typography>
        )}
      </Box>
    </Dialog>
  );
}
